/**
 * <RowContextMenu> — a lightweight right-click menu for workspace tree rows
 * (workspace-file-aliases: the row actions moved off hover onto right-click, so
 * they stop eating horizontal space + truncating the labels). Fixed-position at
 * the cursor, clamped to the viewport; closes on select / Escape / click-outside.
 */

import { useEffect } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface RowContextMenuItem {
  key: string;
  label: string;
  Icon?: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
  /** Destructive item — rendered in red, and separated from the items above by
   *  a rule. Part of the misclick guard, not decoration: a delete that looks
   *  exactly like "Copiar" one row up is the click this is meant to cost. */
  danger?: boolean;
}

interface RowContextMenuProps {
  /** Viewport coords of the right-click. */
  x: number;
  y: number;
  items: RowContextMenuItem[];
  onClose: () => void;
}

const MENU_W = 200;
const ITEM_H = 30;

export function RowContextMenu({ x, y, items, onClose }: RowContextMenuProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Clamp so the menu stays on-screen near the cursor.
  const left = Math.min(x, window.innerWidth - MENU_W - 4);
  const top = Math.min(y, window.innerHeight - items.length * ITEM_H - 8);

  return (
    <>
      {/* Click-outside / right-click-elsewhere backdrop. */}
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
        className="fixed inset-0 z-40 cursor-default"
      />
      <div
        role="menu"
        data-testid="row-context-menu"
        style={{ position: "fixed", top: Math.max(4, top), left: Math.max(4, left), width: MENU_W }}
        className="z-50 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-900"
      >
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            data-testid={`row-context-menu-${item.key}`}
            disabled={item.disabled}
            onClick={() => {
              item.onSelect();
              onClose();
            }}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors",
              item.danger && "mt-1 border-t border-slate-200 pt-2 dark:border-slate-700",
              item.disabled
                ? "cursor-not-allowed text-slate-300 dark:text-slate-600"
                : item.danger
                  ? "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                  : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800",
            )}
          >
            {item.Icon && <item.Icon size={13} className="shrink-0 opacity-70" />}
            <span className="truncate">{item.label}</span>
          </button>
        ))}
      </div>
    </>
  );
}
